import React, { useEffect, useMemo } from 'react';
import {
  useAppStateCurrentAssetReportStore,
  useAppStateManageAssetGoalsModalDataStore,
  useAppStateManageAssetPortfolioModalDataStore,
  useAppStateManageAssetReportModalDataStore,
  useAppStatePrevAssetReportStore,
  useAppStateOwnerFilterStore,
} from '../../../common/store/AppStateStore.zustand';
import AssetStatusCardBarChart from './AssetStatusCardBarChart';
import { getNetAssetTotalAmount, toAssetStatusCardBarChartData } from '../util/AssetStatusCard.util';
import { numberWithCommas, percentageString } from '../../../common/util/NumberUtils';

const AssetStatusCard = () => {
  const { currentAssetReport } = useAppStateCurrentAssetReportStore();
  const { prevAssetReport } = useAppStatePrevAssetReportStore();
  const { manageAssetReportModalData, setManageAssetReportModalData } = useAppStateManageAssetReportModalDataStore();
  const { ownerFilter } = useAppStateOwnerFilterStore();

  const filteredCurrentReport = useMemo(() => {
    if (!currentAssetReport) return undefined;
    if (!ownerFilter) return currentAssetReport;
    return { ...currentAssetReport, reportItemList: currentAssetReport.reportItemList.filter((item) => item.owner === ownerFilter) };
  }, [currentAssetReport, ownerFilter]);

  const filteredPrevReport = useMemo(() => {
    if (!prevAssetReport) return undefined;
    if (!ownerFilter) return prevAssetReport;
    return { ...prevAssetReport, reportItemList: prevAssetReport.reportItemList.filter((item) => item.owner === ownerFilter) };
  }, [prevAssetReport, ownerFilter]);

  const netAssetTotalAmount = useMemo(() => {
    if (!filteredCurrentReport) return 0;
    return getNetAssetTotalAmount(filteredCurrentReport.reportItemList);
  }, [filteredCurrentReport]);

  const prevNetAssetTotalAmount = useMemo(() => {
    if (!filteredPrevReport) return;
    return getNetAssetTotalAmount(filteredPrevReport.reportItemList);
  }, [filteredPrevReport]);

  const netAssetChangeSummary = useMemo(() => {
    if (!netAssetTotalAmount || !prevNetAssetTotalAmount) return;
    const changeAmount = netAssetTotalAmount - prevNetAssetTotalAmount;
    const changePercent = (changeAmount / prevNetAssetTotalAmount) * 100;
    return {
      changeAmount,
      changePercent,
    };
  }, [netAssetTotalAmount, prevNetAssetTotalAmount]);

  const assetStatusCardBarChartData = useMemo(() => {
    if (!filteredCurrentReport) return;
    return toAssetStatusCardBarChartData(filteredCurrentReport);
  }, [filteredCurrentReport]);

  const handleAssetReportClick = () => {
    setManageAssetReportModalData({ isOpen: true, onClose: () => setManageAssetReportModalData(undefined) });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all duration-300 group relative overflow-hidden ">
      <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/5 rounded-full -translate-y-32 translate-x-32 group-hover:translate-y-[-8rem] transition-transform duration-500"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-600/5 rounded-full translate-y-32 -translate-x-32 group-hover:translate-y-[8rem] transition-transform duration-500"></div>
      <div className="flex justify-between items-center mb-6 relative">
        <div>
          <h3 className="text-xl font-semibold text-gray-800 mb-1 flex items-center">
            <span>자산 현황</span>
          </h3>
          <p className="text-sm text-gray-500">총 자산 현황 및 구성 분석</p>
        </div>
      </div>
      {!currentAssetReport && (
        <div className="flex flex-col items-center justify-center h-64 bg-gray-50/50 rounded-lg p-6">
          <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
            <i className="fas fa-chart-bar text-3xl text-blue-300"></i>
          </div>
          <h4 className="text-lg font-medium text-gray-700 mb-2">자산 데이터가 없습니다</h4>
          <p className="text-sm text-gray-500 text-center max-w-sm">
            새로운 데이터를 추가하여 자산 현황을 확인해보세요.
          </p>
          <button
            className="mt-6 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-all duration-200 flex items-center space-x-2 !rounded-button whitespace-nowrap"
            onClick={handleAssetReportClick}
          >
            <i className="fas fa-plus"></i>
            <span>자산 추가하기</span>
          </button>
        </div>
      )}
      {filteredCurrentReport && (
        <>
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between ">
              <div>
                <span className="text-sm text-gray-500">순 자산</span>
                <div className="text-2xl font-bold text-gray-900 mt-1">{numberWithCommas(netAssetTotalAmount)}원</div>
              </div>
              {netAssetChangeSummary && (
                <div className="text-right">
                  <span className="text-sm text-gray-500">전월 대비</span>
                  <div
                    className={`text-lg font-semibold text-${netAssetChangeSummary.changeAmount > 0 ? 'green' : 'red'}-600 mt-1}`}
                  >
                    {`${netAssetChangeSummary.changeAmount > 0 ? '+' : ''}${numberWithCommas(netAssetChangeSummary.changeAmount)} (${netAssetChangeSummary.changePercent > 0 ? '+' : ''}${percentageString(netAssetChangeSummary.changePercent)})`}
                  </div>
                </div>
              )}
            </div>
          </div>
          {assetStatusCardBarChartData && <AssetStatusCardBarChart {...assetStatusCardBarChartData} />}
        </>
      )}
    </div>
  );
};

export default AssetStatusCard;
